import { useEffect, useMemo } from 'react';
import { NovaMark } from '../../design/Nova';
import { Outlet } from 'react-router-dom';
import { useEngine } from '../../api/context';
import { useTheme } from '../../design/theme';
import { useInspector, InspectorDock, useInspectorRouteReset } from '../../views/Inspector';
import { DropdownMenu, type MenuItemDef } from '../../design/primitives/Overlay';
import { IconButton } from '../../design/primitives/Button';
import { Icon, type IconName } from '../../design/icons';
import { Tooltip } from '../../design/primitives/Overlay';
import { NotificationTray } from '../../layout/NotificationTray';
import { FeedbackButton } from '../../views/FeedbackButton';
import { CommandPalette } from '../../layout/CommandPalette';
import { ShortcutSheet } from '../../layout/shortcuts';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserMenu, useSkinMenuItems } from '../UserMenu';
import { useShell } from '../ShellProvider';
import { useShellChrome } from '../useShellChrome';
import { WindowManagerProvider, useWindows } from '../windows/WindowManager';
import { Window } from '../windows/Window';
import { WindowContent } from '../windows/WindowContent';
import type { ShellProps } from '../types';
import { decodeLayout, encodeLayout } from '../windows/shareLayout';
import { usePageActions } from '../../layout/shortcuts';
import { VersionSkewChip } from '../VersionSkewChip';
import { useToast } from '../../design/primitives/Toast';

// Desktop / windows skin: a menu bar (File/Edit/View/Window/Help), a launcher rail whose
// area menus open routes as draggable windows, the routed <Outlet/> as the always-present
// base pane, floating windows on top and a taskbar. Registers its window-opener with the
// ShellProvider so the grid's "Open in new window" opens a window here. Windowing is desktop-
// only: below 768px it degrades to a single maximized window with the taskbar as a switcher.

export function DesktopShell(props: ShellProps) {
  return (
    <WindowManagerProvider>
      <DesktopInner {...props} />
    </WindowManagerProvider>
  );
}

function DesktopInner({ brandShort = 'Nova', areas, sections }: ShellProps) {
  const wm = useWindows();
  const location = useLocation();
  const theme = useTheme();
  const inspector = useInspector();
  const { modules, signOut } = useEngine();
  const shell = useShell();
  const skinItems = useSkinMenuItems();
  const { paletteOpen, setPaletteOpen, sheetOpen, setSheetOpen } = useShellChrome(sections);
  useInspectorRouteReset(location.pathname);

  // Publish the window opener so screens (grid "Open in new window") can open windows.
  // Depend only on the stable callbacks — registering must not re-trigger this effect.
  const registerWindowOpener = shell.registerWindowOpener;
  const openWindow = wm.open;
  useEffect(() => {
    registerWindowOpener((path, title) => openWindow(path, title));
    return () => registerWindowOpener(null);
  }, [registerWindowOpener, openWindow]);

  // Share the layout: `?desk=` carries the open windows (shareLayout.ts). Opening such a link
  // restores them, then the parameter is dropped so a refresh does not re-apply it.
  const toast = useToast();
  const routerNavigate = useNavigate();
  const deskParam = new URLSearchParams(location.search).get('desk');
  const loadLayout = wm.load;
  useEffect(() => {
    if (!deskParam) return;
    const layout = decodeLayout(deskParam);
    if (layout) loadLayout(layout);
    const rest = new URLSearchParams(location.search);
    rest.delete('desk');
    routerNavigate({ pathname: location.pathname, search: rest.toString() ? `?${rest}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per incoming link
  }, [deskParam]);
  const shareLayout = async () => {
    const url = new URL(window.location.href);
    url.searchParams.set('desk', encodeLayout(wm.windows));
    try {
      await navigator.clipboard.writeText(url.toString());
      toast({ kind: 'success', title: 'Link copied', detail: 'Opening it shows these windows in the same places.' });
    } catch {
      toast({ kind: 'error', title: 'Could not copy the link' });
    }
  };
  usePageActions(wm.windows.some((w) => !w.minimized) ? [{ id: 'share-layout', label: 'Copy link to this window layout', icon: 'link' as IconName, group: 'Windows', run: () => void shareLayout() }] : []);

  const createCommands = useMemo(() => modules.flatMap((m) => m.commands ?? []).filter((c) => c.kind === 'create' || /^(new|create|log)/i.test(c.id)), [modules]);

  const fileMenu: MenuItemDef[] = [
    { heading: true, label: 'New' },
    ...createCommands.map((c) => ({ key: c.id, label: c.label, icon: (c.icon as IconName) ?? 'plus', onSelect: () => c.run({ navigate: (to) => wm.open(to, c.label), create: (r) => wm.open(`/${r}?new=1`, c.label), close: () => undefined }) })),
    ...(createCommands.length ? [{ separator: true, label: '' }] : []),
    { label: 'Close window', icon: 'x', disabled: !wm.focusedId, onSelect: () => wm.focusedId && wm.close(wm.focusedId) },
    { separator: true, label: '' },
    { label: 'Sign out', icon: 'log-out', danger: true, onSelect: () => void signOut?.() },
  ];

  // Edit delegates to the focused window's screen where the DOM allows; otherwise disabled.
  const editMenu: MenuItemDef[] = [
    { label: 'Find…', icon: 'search', hint: '⌘K', onSelect: () => setPaletteOpen(true) },
    { separator: true, label: '' },
    { label: 'Select all', icon: 'check-square', disabled: !wm.focusedId, onSelect: () => wm.focusedId && document.querySelector<HTMLElement>('.hf-window.focused .hf-grid-scroll')?.dispatchEvent(new KeyboardEvent('keydown', { key: 'a', metaKey: true, bubbles: true })) },
  ];

  const viewMenu: MenuItemDef[] = [
    { heading: true, label: 'Theme' },
    { label: 'System', icon: 'monitor', checked: theme.pref === 'system', onSelect: () => theme.setPref('system') },
    { label: 'Light', icon: 'sun', checked: theme.pref === 'light', onSelect: () => theme.setPref('light') },
    { label: 'Dark', icon: 'moon', checked: theme.pref === 'dark', onSelect: () => theme.setPref('dark') },
    { separator: true, label: '' },
    { label: 'Hide inspector', icon: 'panel-right', disabled: !inspector.current, onSelect: () => inspector.close() },
    ...(skinItems.length ? [{ separator: true, label: '' }, ...skinItems] : []),
  ];

  const windowMenu: MenuItemDef[] = [
    { heading: true, label: wm.windows.length ? 'Windows' : 'No open windows' },
    ...wm.windows.map((w) => ({ key: w.id, label: w.title, icon: (w.minimized ? 'square' : 'app-window') as IconName, checked: w.id === wm.focusedId, onSelect: () => wm.restore(w.id) })),
    ...(wm.windows.length ? [{ separator: true, label: '' }, { label: 'Copy link to this layout', icon: 'link' as IconName, onSelect: () => void shareLayout() }, { label: 'Tile', icon: 'grid' as IconName, onSelect: () => wm.tile() }, { label: 'Cascade', icon: 'copy' as IconName, onSelect: () => wm.cascade() }, { label: 'Minimize all', icon: 'minus' as IconName, onSelect: () => wm.minimizeAll() }] : []),
  ];

  const helpMenu: MenuItemDef[] = [
    { label: 'Keyboard shortcuts', icon: 'keyboard', hint: '?', onSelect: () => setSheetOpen(true) },
    { label: 'Search & commands', icon: 'command', hint: '⌘K', onSelect: () => setPaletteOpen(true) },
  ];

  const menus: { label: string; items: MenuItemDef[] }[] = [
    { label: 'File', items: fileMenu },
    { label: 'Edit', items: editMenu },
    { label: 'View', items: viewMenu },
    { label: 'Window', items: windowMenu },
    { label: 'Help', items: helpMenu },
  ];

  return (
    <div className="hf-desk">
      <a href="#hf-main" className="hf-skip">Skip to content</a>
      <div className="hf-menubar" role="menubar" aria-label="Application menu">
        <Link to="/" className="hf-menubar-brand" aria-label="Home">
          <NovaMark size={18} className="mark" />
          <span className="hf-display">{brandShort}</span>
        </Link>
        {menus.map((m) => (
          <DropdownMenu key={m.label} aria-label={m.label} placement="bottom-start" trigger={<button type="button" className="hf-menubar-item">{m.label}</button>} items={m.items} />
        ))}
        <div className="hf-menubar-tools">
          <Tooltip content="Search" kbd="⌘K">
            <IconButton icon="search" label="Search" size="sm" onClick={() => setPaletteOpen(true)} />
          </Tooltip>
          <VersionSkewChip />
          <NotificationTray />
          <FeedbackButton />
          <Tooltip content={theme.resolved === 'dark' ? 'Light mode' : 'Dark mode'} kbd="⌘⇧L">
            <IconButton icon={theme.resolved === 'dark' ? 'sun' : 'moon'} label={theme.resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} size="sm" onClick={theme.toggle} />
          </Tooltip>
          <UserMenu placement="bottom-end" onShortcuts={() => setSheetOpen(true)} />
        </div>
      </div>

      <div className="hf-desk-surface">
        <nav className="hf-desk-launch" aria-label="Areas">
          {areas.map((a) => (
            <DropdownMenu
              key={a.id}
              aria-label={`${a.label} — open a window`}
              placement="right-start"
              trigger={
                <button type="button" className="hf-desk-launch-btn" title={a.label}>
                  <Icon name={a.icon} size={20} />
                  <span className="lbl">{a.label}</span>
                </button>
              }
              items={[
                { heading: true, label: a.label },
                ...a.sections.flatMap((s) => s.items.map((it) => ({ key: it.to, label: it.label, icon: it.icon ?? ('circle-dot' as IconName), onSelect: () => wm.open(it.to, it.label) }))),
              ]}
            />
          ))}
        </nav>

        <main id="hf-main" className="hf-desk-base hf-scroll" tabIndex={-1}>
          <Outlet />
        </main>
        <InspectorDock />

        {wm.windows.map((w) => (
          <Window key={w.id} win={w} focused={w.id === wm.focusedId}>
            <WindowContent win={w} />
          </Window>
        ))}
      </div>

      <div className="hf-taskbar" role="toolbar" aria-label="Open windows">
        {wm.windows.length === 0 ? (
          <span className="hf-taskbar-empty">Use the rail or ⌘K to open a window</span>
        ) : (
          wm.windows.map((w) => (
            <button
              key={w.id}
              type="button"
              className={['hf-taskbar-item', w.id === wm.focusedId && !w.minimized && 'active', w.minimized && 'min'].filter(Boolean).join(' ')}
              aria-pressed={w.id === wm.focusedId && !w.minimized}
              onClick={() => (w.minimized || w.id !== wm.focusedId ? wm.restore(w.id) : wm.minimize(w.id))}
            >
              <Icon name="app-window" size={13} />
              <span className="lbl">{w.title}</span>
            </button>
          ))
        )}
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ShortcutSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </div>
  );
}
